import { makeStyles } from '@material-ui/core/styles';
import { Handle, Position } from '@xyflow/react';
import classnames from 'classnames';
import hexToRgba from 'hex-rgba';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../app/theme';
import { ConditionsType, NodeType } from '../../app/types';
import { changeSeconds } from '../../app/utils';
import IconEnter from '../../assets/images/Icons/IconEnter';
import clickImage from '../../assets/images/svg/ic_click_20.svg';
import emojiImage from '../../assets/images/svg/ic_emoji.svg';
import nEmojiImage from '../../assets/images/svg/ic_emoji_no.svg';
import unclickImage from '../../assets/images/svg/ic_unclick_20.svg';
import {
  checkNode,
  setDefaultTemplate,
  setIsShowSettingBar,
  setSelectedNode,
} from '../../features/mainSlice';
import Tooltip from '../shared/Tooltip';

const useStyles = makeStyles(
  {
    root: {
      width: 240,
      borderRadius: '12px',
      boxShadow: '0 8px 36px 0 rgba(0, 0, 0, 0.06)',
      border: 0,
    },
    selected: {
      border: `solid 1px ${color.$15}`,
    },
    error: {
      border: `solid 1px ${color.$17}`,
    },
    highlightPath: {
      border: `solid 1px ${color.$3}!important`,
    },
    handle: {
      border: '0 !important',
      borderRadius: '0 !important',
      background: `${color.$3} !important`,
      width: '1px !important',
      height: '1px !important',
      minWidth: '1px !important',
      minHeight: '1px !important',
    },
    title: {
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
      fontSize: '13px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$15,
      display: 'flex',
      alignItems: 'center',
      padding: '8px 16px',
      background: hexToRgba(color.$15, 6),
      '& > svg': {
        marginRight: 2,
      },
    },
    errorTitle: {
      color: color.$17,
      background: hexToRgba(color.$17, 6),
    },
    content: {
      padding: '12px 16px',
    },
    condition: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1.38',
      letterSpacing: 'normal',
      color: color.$3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 0',
      background: color.$19,
      '& > img': {
        marginRight: 4,
      },
    },
    conditionTooltip: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    index: {
      borderRadius: 20,
      background: color.$6,
      color: color.$7,
      fontSize: '13px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      textAlign: 'center',
      height: 20,
      padding: '4px 8px',
      marginLeft: 4,
    },
  },
  {
    name: 'Condition',
  },
);

const ConditionComponent = ({ id, data, isConnectable }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isErrorNodeIds = useSelector(store => store.ma.isErrorNodeIds);
  const selectedNode = useSelector(store => store.ma.selectedNode);
  const nodes = useSelector(store => store.ma.data?.nodes) || [];
  const allPathNodes = useSelector(store => store.ma.highlightPathNodes) || [];
  const isPreview = useSelector(store => store.ma.isPreview);
  const buttons = data?.buttons;

  const targetNode = useMemo(
    () => nodes.find(n => n?.id === id) || {},
    [nodes],
  );

  const groupNode = useMemo(
    () => nodes.find(n => n?.id === data?.groupId) || {},
    [nodes],
  );

  const { timeType, time } = groupNode?.data || {};

  const isHighlightPath = isPreview && !!allPathNodes.find(n => n?.id === id);
  const isSelected = selectedNode?.id === data?.groupId;

  const isStickerCondition = groupNode?.data?.type === ConditionsType.STICKER;

  const isError = isStickerCondition
    ? !!~isErrorNodeIds.indexOf(data?.groupId)
    : !!~isErrorNodeIds.indexOf(data?.groupId) &&
      !checkNode({ type: NodeType.CONDITION, data });

  const onSelect = () => {
    if (!isSelected && groupNode) {
      dispatch(setSelectedNode(isPreview ? targetNode : groupNode));
      dispatch(
        setDefaultTemplate({
          isShow: false,
        }),
      );
    }

    dispatch(setIsShowSettingBar(true));
  };

  const timeText = useMemo(() => {
    return t(`condition-node.${timeType}`, {
      time: changeSeconds(time, timeType) || 30,
    });
  }, [timeType, time]);

  const content = useMemo(() => {
    switch (data?.type) {
      case 'click':
      case 'n-click':
        return typeof buttons === 'object' && buttons.length ? (
          <Tooltip
            light
            placement="top"
            isComponent
            title={
              <div className={classes.conditionTooltip}>
                {typeof buttons === 'object' &&
                  (buttons || []).map(b => (
                    <div key={`button-${b?.id}`} className={classes.index}>
                      {b?.index}
                    </div>
                  ))}
              </div>
            }
          >
            <div className={classes.condition}>
              <img
                src={data?.type === 'click' ? clickImage : unclickImage}
                alt="click"
              />
              {timeText}
              {t(`condition-node.${data?.type}`)}
              {buttons &&
                (buttons || []).slice(0, 2).map(b => (
                  <div key={`button-${b?.id}`} className={classes.index}>
                    {b?.index}
                  </div>
                ))}
              {buttons && buttons.length > 2 && (
                <div style={{ marginLeft: 4 }}>...</div>
              )}
            </div>
          </Tooltip>
        ) : (
          <div className={classes.condition}>
            <img
              src={data?.type === 'click' ? clickImage : unclickImage}
              alt="click"
            />
            {timeText}
            {t(`condition-node.${data?.type}`)}
          </div>
        );
      case 'right-sticker':
      case 'wrong-sticker':
      case 'n-sticker':
        return (
          <div className={classes.condition}>
            <img
              src={data?.type === 'right-sticker' ? emojiImage : nEmojiImage}
              alt="click"
            />
            {timeText}
            {t(`condition-node.${data?.type}`)}
          </div>
        );
      default:
        return null;
    }
  }, [timeText, data]);

  return (
    <div>
      <Handle
        className={classes.handle}
        type="target"
        position={Position.Top}
        style={{ top: 0 }}
        isConnectable={isConnectable}
      />
      <div
        className={classnames(classes.root, {
          [classes.selected]: isSelected,
          [classes.error]: isError,
          [classes.highlightPath]: isHighlightPath,
        })}
        onClick={onSelect}
      >
        <div
          className={classnames(classes.title, {
            [classes.errorTitle]: isError,
          })}
        >
          <IconEnter backgroundColor={isError ? color.$17 : color.$15} />
          {t('add-conditions')}
        </div>
        <div className={classes.content}>{content}</div>
      </div>
      <Handle
        className={classes.handle}
        type="source"
        position={Position.Bottom}
        style={{ bottom: 0 }}
        isConnectable={isConnectable}
      />
    </div>
  );
};

export default ConditionComponent;
